import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Grid, List, Filter, Download, CheckCircle2, XCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

type ViewMode = 'table' | 'card';
type BookingType = 'all' | 'marketplace';
type BookingStatus = 'all' | 'pending' | 'negotiating' | 'awaiting-approval' | 'vendor-booked' | 'confirmed' | 'cancelled' | 'rejected' | 'expired';

interface BookingFilters {
  search: string;
  organization: string;
  type: BookingType;
  status: BookingStatus;
}

interface BookingFiltersProps {
  filters: BookingFilters;
  onFiltersChange: (filters: BookingFilters) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  selectedCount: number;
  onBulkAction: (action: string) => void;
}

export default function BookingFilters({
  filters,
  onFiltersChange,
  viewMode,
  onViewModeChange,
  selectedCount,
  onBulkAction
}: BookingFiltersProps) {
  return (
    <Card className="athlyn-card">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search bookings..."
              value={filters.search}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
              className="pl-10"
              aria-label="Search bookings"
            />
          </div>

          {/* Organization Filter */}
          <Select value={filters.organization} onValueChange={(value) => onFiltersChange({ ...filters, organization: value })}>
            <SelectTrigger className="w-full lg:w-48" aria-label="Filter by organization">
              <SelectValue placeholder="All Organizations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={filters.type} onValueChange={(value) => onFiltersChange({ ...filters, type: value as BookingType })}>
            <SelectTrigger className="w-full lg:w-40" aria-label="Filter by type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="marketplace">Marketplace</SelectItem>
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={filters.status} onValueChange={(value) => onFiltersChange({ ...filters, status: value as BookingStatus })}>
            <SelectTrigger className="w-full lg:w-48" aria-label="Filter by status">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="negotiating">Negotiating</SelectItem>
              <SelectItem value="awaiting-approval">Awaiting Approval</SelectItem>
              <SelectItem value="vendor-booked">Vendor Booked</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange('table')}
                className="w-9 h-9 p-0"
                aria-label="Table view"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'card' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange('card')}
                className="w-9 h-9 p-0"
                aria-label="Card view"
              >
                <Grid className="w-4 h-4" />
              </Button>
            </div>

            {/* Bulk Actions */}
            {selectedCount > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Actions ({selectedCount})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onBulkAction('confirm')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirm Selected
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onBulkAction('cancel')}>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Selected
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onBulkAction('export')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Selected
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}